import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../../data/mock';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] mb-4">TESTIMONIALS</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">
            Words From <span className="italic text-[#D4AF37]">Partners</span>
          </h2>
        </div>

        {/* Testimonial Carousel */}
        <div
          className={`relative transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          {/* Quote Icon */}
          <Quote
            className="absolute -top-4 left-1/2 -translate-x-1/2 text-[#D4AF37]/20"
            size={80}
            strokeWidth={1}
          />

          {/* Testimonial Content */}
          <div className="relative bg-[#121212] border border-[#D4AF37]/10 p-8 md:p-16 text-center">
            <div className="relative overflow-hidden">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={`transition-all duration-700 ease-out ${
                    index === activeIndex
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-full'
                  }`}
                >
                  <p className="font-playfair text-xl md:text-2xl text-white leading-relaxed mb-10 italic">
                    "{testimonial.quote}"
                  </p>

                  <div>
                    <p className="text-[#D4AF37] text-sm tracking-[0.2em]">
                      {testimonial.author.toUpperCase()}
                    </p>
                    <p className="text-[#A1A1AA] text-sm mt-1">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-center items-center gap-6 mt-12">
              <button
                onClick={prevTestimonial}
                className="p-3 border border-[#D4AF37]/20 hover:border-[#D4AF37] text-[#A1A1AA] hover:text-[#D4AF37] transition-all duration-300"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Dots */}
              <div className="flex gap-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-2 h-2 transition-all duration-300 ${
                      index === activeIndex
                        ? 'bg-[#D4AF37] w-8'
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="p-3 border border-[#D4AF37]/20 hover:border-[#D4AF37] text-[#A1A1AA] hover:text-[#D4AF37] transition-all duration-300"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
